import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import { RingoverCallOutcome, RingoverDialerService } from './ringover-dialer.service';

@Injectable({
  providedIn: 'root',
})
export class PotentialClientCallService {
  private api = inject(ApiService);
  private ringover_dialer = inject(RingoverDialerService);
  private snack_bar = inject(MatSnackBar);

  /**
   * Dial lead, wait for hangup.
   * - No answer → action call_no_answer
   * - Answered → acta + action call (audio/summary arrive via Ringover webhooks)
   */
  async callLeadAndProcess(potential_client_id: number | string, phone: string, on_done?: () => void): Promise<void> {
    if (!phone || !this.ringover_dialer.isEnabledForCurrentUser()) {
      return;
    }

    try {
      const outcome = await firstValueFrom(this.ringover_dialer.callWithOutcome(phone));
      await this.handleOutcome(potential_client_id, outcome, on_done);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo iniciar la llamada';
      this.snack_bar.open(message, 'Cerrar', { duration: 6000 });
    }
  }

  private async handleOutcome(potential_client_id: number | string, outcome: RingoverCallOutcome, on_done?: () => void): Promise<void> {
    if (!outcome.answered) {
      await firstValueFrom(
        this.api.createPotentialClientAction(potential_client_id, {
          type: 'call_no_answer',
          notes: outcome.call_id ? `Ringover CallID: ${outcome.call_id}` : undefined,
        })
      );
      this.snack_bar.open('Acción «Llamada no contesta» registrada', 'Cerrar', { duration: 4000 });
      on_done?.();
      return;
    }

    if (!outcome.call_id) {
      await firstValueFrom(this.api.createPotentialClientAction(potential_client_id, { type: 'call' }));
      this.snack_bar.open('Llamada registrada, pero Ringover no devolvió call_id; no se podrá adjuntar audio/resumen', 'Cerrar', {
        duration: 6000,
      });
      on_done?.();
      return;
    }

    try {
      const result = await firstValueFrom(
        this.api.ingestRingoverCallRecording(potential_client_id, {
          call_id: outcome.call_id,
          called_at: new Date().toISOString(),
        })
      );

      const title = result.meeting_record?.title ?? 'Acta';
      this.snack_bar.open(`Acta «${title}» creada. Audio y resumen se adjuntarán cuando Ringover los notifique`, 'Cerrar', {
        duration: 6000,
      });
      on_done?.();
    } catch (err: unknown) {
      await firstValueFrom(
        this.api.createPotentialClientAction(potential_client_id, {
          type: 'call',
          notes: `Ringover CallID: ${outcome.call_id}`,
        })
      );
      const message =
        (err as { error?: { error?: string; message?: string } })?.error?.error ||
        (err as { error?: { message?: string } })?.error?.message ||
        (err instanceof Error ? err.message : 'No se pudo crear el acta');
      this.snack_bar.open(String(message), 'Cerrar', { duration: 7000 });
      on_done?.();
    }
  }
}
